import type { ColumnDef, EnemyDef, LevelDef, PickupDef, SegmentDef } from '../../types';

// OP-103 La Catedral (GDD §10.3, issue #99): the night approach to the end of world 1, written by hand with the
// format of §15.6. 400 u of approach at 5.5 u/s (takeoff, warm-up 18 %, tension 26 % up to CP-103-A at x 200,
// respite 15 %, peak 41 %), CP-103-B 5 u before the arena, the arena of JEFE-1 (open length: the fight ends
// it, #92), the US sky of 120 u with bonus bills and the jump zone. Hazards of the approach: cable cars (EN-12)
// above or below the safe route, the search bloc (EN-02) hovering high in the peak and one hitman plane (EN-03)
// announced with INTERCEPT; an armor before the checkpoint and a bribe before the helicopters. All x are local.

const column = (x: number, gapY: number, skin: 'w1-power-tower' | 'w1-rock-houses'): ColumnDef => ({ x, gapY, gapH: 4.0, skin });
const dollar = (x: number, y: number): PickupDef => ({ x, y, type: 'dollar' });
const armor = (x: number, y: number): PickupDef => ({ x, y, type: 'armor' });
const bribe = (x: number, y: number): PickupDef => ({ x, y, type: 'bribe' });
/** A cable car between two mountains: the lower end at (x, y), the upper one at (x2, y2). */
const cableCar = (x: number, y: number, x2: number, y2: number): EnemyDef => ({ x, y, type: 'EN-12', params: { x2, y2 } });
/** A helicopter of the search bloc (Coronel Carrizo's squadron, §4.4), hovering high. */
const searchBloc = (x: number, y: number): EnemyDef => ({ x, y, type: 'EN-02' });
const hitman = (x: number, y: number): EnemyDef => ({ x, y, type: 'EN-03' });

const takeoff: SegmentDef = { id: 'w1-catedral-takeoff-01', kind: 'takeoff', length: 40, columns: [], enemies: [], pickups: [] };

const warmup01: SegmentDef = {
  id: 'w1-catedral-warmup-01',
  kind: 'pattern',
  phase: 'warmup',
  length: 66,
  columns: [
    column(20.0, 6.0, 'w1-power-tower'),
    column(29.0, 6.5, 'w1-rock-houses'),
    column(38.0, 6.0, 'w1-power-tower'),
    column(47.0, 5.5, 'w1-rock-houses'),
    column(56.0, 6.0, 'w1-power-tower'),
    column(65.0, 6.5, 'w1-rock-houses'),
  ],
  enemies: [],
  pickups: [
    dollar(23.5, 6.25),
    dollar(24.5, 6.55),
    dollar(25.5, 6.25),
    dollar(50.5, 5.75),
    dollar(51.5, 6.05),
    dollar(52.5, 5.75),
  ],
};

const tension01: SegmentDef = {
  id: 'w1-catedral-tension-01',
  kind: 'pattern',
  phase: 'tension',
  length: 47,
  columns: [
    column(8.0, 7.0, 'w1-power-tower'),
    column(17.0, 5.5, 'w1-rock-houses'),
    column(26.0, 7.5, 'w1-power-tower'),
    column(35.0, 6.0, 'w1-rock-houses'),
    column(44.0, 8.0, 'w1-power-tower'),
  ],
  enemies: [
    cableCar(28.0, 2.25, 33.0, 3.75),
  ],
  pickups: [
    dollar(20.5, 6.50),
    dollar(21.5, 6.80),
    dollar(22.5, 6.50),
    armor(39.5, 7.00),
  ],
};

const tension02: SegmentDef = {
  id: 'w1-catedral-tension-02',
  kind: 'pattern',
  phase: 'tension',
  length: 47,
  columns: [
    column(6.0, 6.5, 'w1-power-tower'),
    column(15.0, 5.0, 'w1-rock-houses'),
    column(24.0, 7.0, 'w1-power-tower'),
    column(33.0, 5.5, 'w1-rock-houses'),
    column(42.0, 6.5, 'w1-power-tower'),
  ],
  enemies: [
    cableCar(26.0, 9.25, 31.0, 10.75),
  ],
  pickups: [
    dollar(18.5, 6.00),
    dollar(19.5, 6.30),
    dollar(20.5, 6.00),
  ],
};

const respite01: SegmentDef = {
  id: 'w1-catedral-respite-01',
  kind: 'pattern',
  phase: 'respite',
  length: 54,
  columns: [
    column(4.0, 6.0, 'w1-power-tower'),
    column(13.0, 6.5, 'w1-rock-houses'),
    column(22.0, 6.0, 'w1-power-tower'),
    column(31.0, 6.5, 'w1-rock-houses'),
    column(40.0, 6.0, 'w1-power-tower'),
    column(49.0, 6.5, 'w1-rock-houses'),
  ],
  enemies: [],
  pickups: [
    dollar(7.5, 6.25),
    dollar(8.5, 6.55),
    dollar(9.5, 6.25),
    dollar(34.5, 6.25),
    dollar(35.5, 6.55),
    dollar(36.5, 6.25),
    bribe(17.5, 6.25),
  ],
};

const peak01: SegmentDef = {
  id: 'w1-catedral-peak-01',
  kind: 'pattern',
  phase: 'peak',
  length: 73,
  columns: [
    column(4.0, 8.5, 'w1-power-tower'),
    column(13.0, 6.0, 'w1-rock-houses'),
    column(22.0, 9.0, 'w1-power-tower'),
    column(31.0, 6.5, 'w1-rock-houses'),
    column(40.0, 4.5, 'w1-power-tower'),
    column(49.0, 7.5, 'w1-rock-houses'),
    column(58.0, 5.0, 'w1-power-tower'),
    column(67.0, 8.0, 'w1-rock-houses'),
  ],
  enemies: [
    cableCar(51.0, 9.25, 56.0, 10.75),
    hitman(46.0, 6),
  ],
  pickups: [
    dollar(25.5, 7.75),
    dollar(26.5, 8.05),
    dollar(27.5, 7.75),
  ],
};

const peak02: SegmentDef = {
  id: 'w1-catedral-peak-02',
  kind: 'pattern',
  phase: 'peak',
  length: 73,
  columns: [
    column(3.0, 5.5, 'w1-power-tower'),
    column(12.0, 8.5, 'w1-rock-houses'),
    column(21.0, 6.0, 'w1-power-tower'),
    column(30.0, 4.0, 'w1-rock-houses'),
    column(39.0, 7.0, 'w1-power-tower'),
    column(48.0, 6.0, 'w1-rock-houses'),
    column(57.0, 6.5, 'w1-power-tower'),
  ],
  enemies: [
    searchBloc(16.5, 11.2),
    searchBloc(43.5, 11.2),
  ],
  pickups: [
    dollar(15.5, 7.25),
    dollar(16.5, 7.55),
    dollar(17.5, 7.25),
    dollar(42.5, 6.50),
    dollar(43.5, 6.80),
    dollar(44.5, 6.50),
  ],
};

/** The arena of JEFE-1: no columns; the fight lasts until the boss retreats (§9.1). */
const arena: SegmentDef = { id: 'w1-catedral-arena-01', kind: 'boss-arena', length: 600, columns: [], enemies: [], pickups: [] };

/** The sky of Miami (§11.1): 120 u of free flight with bonus bills. */
const usSky: SegmentDef = {
  id: 'w1-catedral-us-sky-01',
  kind: 'us-sky',
  length: 120,
  columns: [],
  enemies: [],
  pickups: [dollar(20, 6), dollar(21, 6.4), dollar(22, 6.8), dollar(60, 8), dollar(61, 8.4), dollar(62, 8.8), dollar(100, 6), dollar(101, 5.6), dollar(102, 5.2)],
};

/** The jump zone of El Salto (§11.1 steps 2-6): the X on the ground and room for the fall. */
const jumpZone: SegmentDef = { id: 'w1-catedral-jump-01', kind: 'jump', length: 60, columns: [], enemies: [], pickups: [] };

export const OP_103_SEGMENTS: readonly SegmentDef[] = [takeoff, warmup01, tension01, tension02, respite01, peak01, peak02, arena, usSky, jumpZone];

export const OP_103: LevelDef = {
  id: '1-3',
  op: 'OP-103',
  name: 'La Catedral',
  world: 1,
  scrollSpeed: 5.5,
  timeOfDay: 'night',
  segments: OP_103_SEGMENTS.map((segment) => segment.id),
  checkpoints: [
    { id: 'CP-103-A', x: 200 },
    { id: 'CP-103-B', x: 395 },
  ],
  ending: 'boss-and-jump',
  bossId: 'JEFE-1',
};
